import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_STORAGE_KEY = 'cigp-theme-preference';
  private readonly themeSubject$ = new BehaviorSubject<Theme>(this.getInitialTheme());

  public theme$: Observable<Theme> = this.themeSubject$.asObservable();

  constructor() {
    // Apply initial theme on service creation
    this.applyTheme(this.themeSubject$.value);
  }

  /**
   * Get the initial theme from localStorage or system preference
   */
  private getInitialTheme(): Theme {
    // Check localStorage first
    const savedTheme = localStorage.getItem(this.THEME_STORAGE_KEY);
    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme;
    }

    // Fall back to system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    // Default to light theme
    return 'light';
  }

  /**
   * Get the current theme
   */
  public getCurrentTheme(): Theme {
    return this.themeSubject$.value;
  }

  /**
   * Toggle between light and dark theme
   */
  public toggleTheme(): void {
    const newTheme: Theme = this.themeSubject$.value === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /**
   * Set a specific theme
   */
  public setTheme(theme: Theme): void {
    this.themeSubject$.next(theme);
    this.applyTheme(theme);
    localStorage.setItem(this.THEME_STORAGE_KEY, theme);
  }

  /**
   * Apply the theme to the document
   */
  private applyTheme(theme: Theme): void {
    const body = document.body;
    const html = document.documentElement;

    // Remove existing theme classes
    body.classList.remove('theme-light', 'theme-dark');
    html.classList.remove('theme-light', 'theme-dark');

    // Add new theme class
    body.classList.add(`theme-${theme}`);
    html.classList.add(`theme-${theme}`);

    // Update data attribute for easier CSS targeting
    html.setAttribute('data-theme', theme);

    // Update Bootstrap color mode attribute (Bootstrap 5.3+)
    html.setAttribute('data-bs-theme', theme);

    // Dynamically load the theme CSS file
    this.loadThemeStylesheet(theme);
  }

  /**
   * Dynamically load the theme-specific CSS file by injecting/updating a <link> element
   */
  private loadThemeStylesheet(theme: Theme): void {
    const themeLink = document.getElementById('cigp-theme-css') as HTMLLinkElement;
    const themePath =
      theme === 'dark'
        ? './assets/sf-bs-cigp-styles/bootstrap5.3-dark.min.css'
        : './assets/sf-bs-cigp-styles/bootstrap5.3.min.css';

    if (themeLink) {
      // Update existing link href to switch themes
      themeLink.href = themePath;
    } else {
      // Create new link element if it doesn't exist
      const head = document.getElementsByTagName('head')[0];
      const newLink = document.createElement('link');
      newLink.id = 'cigp-theme-css';
      newLink.rel = 'stylesheet';
      newLink.type = 'text/css';
      newLink.href = themePath;
      head.appendChild(newLink);
    }
  }

  /**
   * Check if dark mode is active
   */
  public isDarkMode(): boolean {
    return this.themeSubject$.value === 'dark';
  }
}
